import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { PrimaryButton } from "@shared/buttons";
import { ErrorDisplay, SuccessDisplay } from "@shared/notifications";
import Logo from "@shared/Logo";
import { toast } from "react-toastify";
import { addCookie } from "@utils/cookies";
import {
  login,
  register,
  getProfile,
  requestSignupOTP,
  verifySignupOTP,
  googleSignup,
} from "@services/krent.service";
import { GoogleSignIn } from "@shared/GoogleSignIn";
import {
  persistGoogleSession,
  GoogleAuthResult,
} from "@utils/completeGoogleAuth";
import { goToPostAuthDestination, resolvePostAuthDestination } from "@utils/postAuthRedirect";
import {
  getPasswordValidationError,
  isPasswordValid,
  PASSWORD_REQUIREMENTS_HINT,
} from "@utils/passwordValidation";
import { getPhoneValidationError } from "@utils/phoneValidation";

interface BuyerSignupFlowProps {
  onClose: () => void;
  onBackToSelection?: () => void;
  hideBackButton?: boolean;
}

export const BuyerSignupFlow = ({
  onClose,
  onBackToSelection,
  hideBackButton,
}: BuyerSignupFlowProps) => {
  const navigate = useNavigate();

  const checkAndRedirectToPendingBooking = (profileUser?: any) => {
    if (!sessionStorage.getItem("pendingBooking") && !localStorage.getItem("postLoginRedirect")) return false;
    goToPostAuthDestination(navigate, resolvePostAuthDestination(profileUser));
    return true;
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const passwordValidationError = getPasswordValidationError(password);
  const passwordIsValid = isPasswordValid(password);
  const passwordsMatch =
    password === confirmPassword && confirmPassword.trim().length > 0;
  const canSubmitPassword =
    passwordIsValid && passwordsMatch && agreeToTerms;

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  useEffect(() => {
    if (!error) return;
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [error]);

  const handleBackToSelection = () => {
    if (onBackToSelection) {
      onBackToSelection();
    } else {
      onClose();
    }
  };

  const handleStep1Submit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const phoneError = getPhoneValidationError(phoneNumber);
      const validations = [
        {
          condition: !firstname.trim(),
          message: "Please enter your first name",
        },
        { condition: !lastname.trim(), message: "Please enter your last name" },
        {
          condition: !email.trim(),
          message: "Please enter your email address",
        },
        {
          condition: Boolean(phoneError),
          message: phoneError || "Please enter a valid phone number",
        },
      ];

      for (const { condition, message } of validations) {
        if (condition) {
          setError(message);
          setLoading(false);
          return;
        }
      }

      const response = await requestSignupOTP(email);
      if (response.success) {
        setCurrentStep(2);
        setCountdown(60);
      }
    } catch (err: any) {
      const errorMessage =
        typeof err === "string"
          ? err
          : err?.message || "Something went wrong. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async () => {
    try {
      setLoading(true);
      setOtpError("");

      const verifyRes = await verifySignupOTP(email, otp);
      if (!verifyRes.success) {
        setOtpError(verifyRes.message || "Invalid OTP. Please try again.");
      } else {
        setCurrentStep(3);
        toast.success("Email verified successfully!");
      }
    } catch (err: any) {
      const serverError = err?.response?.data?.errors?.[0]?.message;
      setOtpError(serverError || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setLoading(true);
      setOtpError("");
      const response = await requestSignupOTP(email);
      if (response.success) {
        toast.success("OTP resent successfully!");
        setCountdown(60);
      }
    } catch (err: any) {
      setOtpError("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const passwordError = passwordValidationError;

      const validations = [
        {
          condition: Boolean(passwordError),
          message: passwordError || "",
        },
        {
          condition: !confirmPassword.trim(),
          message: "Please confirm your password",
        },
        {
          condition: password !== confirmPassword,
          message: "Passwords do not match",
        },
      ];

      for (const { condition, message } of validations) {
        if (condition) {
          setError(message);
          setLoading(false);
          return;
        }
      }

      const data = {
        firstname,
        lastname,
        email,
        phone: phoneNumber,
        password,
        passwordConfirm: confirmPassword,
        role: "user",
      };

      const registerRes = await register(data);

      if (registerRes.success) {
        setSuccess(registerRes.message || "Registration successful");

        try {
          const loginRes = await login(email, password);

          if (loginRes?.token) {
            addCookie("1Q_SPA", loginRes.token, 24000);

            const abortController = new AbortController();
            const profileRes: any = await getProfile(abortController.signal);

            if (profileRes?.data) {
              sessionStorage.setItem("user", JSON.stringify(profileRes.data));
            }

            onClose();
            if (checkAndRedirectToPendingBooking(profileRes?.data || profileRes)) {
              return;
            }
            navigate("/explore-listings", {
              replace: true,
              state: { verified: true },
            });
          }
        } catch (loginErr) {
          toast.success(
            "Registration successful! Please sign in with your credentials.",
          );
          onClose();
          navigate("/?auth=login", {
            state: {
              registrationSuccess: true,
              email: email,
            },
          });
        }
      }
    } catch (err: any) {
      if (err?.errors && Array.isArray(err.errors)) {
        const errorMessages = err.errors.map((error: any) => {
          const message = error.message || error;
          if (message.toLowerCase().includes("email already in use")) {
            return "This email is already registered. Please use a different email or sign in.";
          } else if (message.toLowerCase().includes("phone")) {
            return "Please check your phone number format.";
          } else if (message.toLowerCase().includes("password")) {
            return "Please ensure your password meets the requirements.";
          }
          return message;
        });
        setError(errorMessages.join(", "));
      } else {
        const errorMessage =
          typeof err === "string"
            ? err
            : err?.message || "Something went wrong. Please try again.";
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async (response: any) => {
    try {
      setError("");

      const registerRes = (await googleSignup({
        id_token: response.credential,
        role: "user",
      })) as GoogleAuthResult;

      if (!registerRes.success || !registerRes.token) {
        throw new Error("Google signup failed");
      }

      setSuccess("Account created successfully with Google!");
      const profileUser = await persistGoogleSession(registerRes);
      onClose();
      if (checkAndRedirectToPendingBooking(profileUser || registerRes)) {
        return;
      }
      navigate("/explore-listings", { replace: true });
    } catch (err: any) {
      const errorMessage =
        err?.message || "Google signup failed. Please try again.";
      setError(errorMessage);
    }
  };

  if (currentStep === 1) {
    return (
      <div
        ref={scrollRef}
        className="px-4 pb-3"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <div className=" d-flex flex-column mb-3">
          <Logo />

          <div>
            {!hideBackButton && (
              <button
                onClick={handleBackToSelection}
                className="btn btn-link text-decoration-none p-0 fw-semibold fs-14 mt-3"
                style={{ color: "#47BBA9" }}
              >
                ← Back
              </button>
            )}
          </div>
        </div>

        <h4 className="fw-bolder font-onest mb-2">Create a buyer account</h4>
        <p className="text-light-black fs-14 mb-4 font-onest">
          Basic Information
        </p>

        <div ref={errorRef}>
          <ErrorDisplay
            errors={
              Array.isArray(error)
                ? error
                : error
                  ? [{ message: String(error), field: "general" }]
                  : []
            }
          />
        </div>
        <SuccessDisplay message={success} />

        <form className="mb-children mt-3" onSubmit={handleStep1Submit}>
          <div className="mb-3">
            <label className="text-uppercase fw-600 fs-12 m-1">
              Legal Name
            </label>
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <input
                type="text"
                className="form-control border-0 rounded-0 ps-3 py-3 fs-14"
                placeholder="First name"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                style={{ backgroundColor: "#f8f9fa" }}
                required
              />
              <div style={{ height: "1px", backgroundColor: "#dee2e6" }} />
              <input
                type="text"
                className="form-control border-0 rounded-0 ps-3 py-3 fs-14"
                placeholder="Last name"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                style={{ backgroundColor: "#f8f9fa" }}
                required
              />
            </div>
          </div>
          <div className="col mb-3">
            <label className="text-uppercase fw-600 fs-12 m-1">
              Phone Number
            </label>
            <input
              type="tel"
              className="form-control border rounded-3 ps-3 py-3 fs-14"
              style={{ backgroundColor: "#f8f9fa" }}
              placeholder="Enter your phone number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
              maxLength={11}
            />
          </div>
          <div className="col mb-3">
            <label className="text-uppercase fw-600 fs-12 m-1">
              Email Address
            </label>
            <input
              type="email"
              className="form-control border rounded-3 ps-3 py-3 fs-14"
              style={{ backgroundColor: "#f8f9fa" }}
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="d-grid">
            <PrimaryButton type="primary" text="Proceed" loading={loading} />
          </div>
        </form>

        <div className="text-center my-4">
          <span className="text-muted fs-14">OR</span>
        </div>

        <div className="d-grid">
          <div
            className="d-flex align-items-center justify-content-center p-2 rounded-3"
            style={{
              minHeight: "52px",
              backgroundColor: "#ffffff",
              overflow: "hidden",
            }}
          >
            <GoogleSignIn
              onSuccess={handleGoogleSignup}
              onError={setError}
              buttonId="google-signin-button"
              text="signup_with"
              width={400}
            />
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 2) {
    return (
      <div
        className="px-4 pb-3"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <div className=" d-flex flex-column mb-3">
          <Logo />

          <div>
            <button
              onClick={() => setCurrentStep(1)}
              className="btn btn-link text-decoration-none p-0 fw-semibold fs-14 mt-3"
              style={{ color: "#47BBA9" }}
            >
              ← Back
            </button>
          </div>
        </div>

        <div className="mb-4">
          <h5 className="fw-semibold fs-18 font-onest mb-2">
            Check your mail, we have sent you a code.
          </h5>

          <p className="mb-3 fs-14">
            A 6 digit code has been sent to {email}.<br /> Enter it below to
            verify and continue.
          </p>
        </div>

        {otpError && (
          <div className="alert alert-danger fs-14 py-2">{otpError}</div>
        )}

        <div className="mb-3">
          <label className="text-uppercase fw-600 fs-12 m-1">Enter OTP</label>
          <input
            type="text"
            className="form-control border rounded-3 ps-3 py-3 fs-14"
            placeholder="Enter 6-digit OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength={6}
          />
        </div>

        <div className="d-grid gap-2 mt-3">
          <PrimaryButton
            type="primary"
            text="Verify"
            loading={loading}
            click={handleOtpVerify}
          />
          <button
            type="button"
            className="text-primary bg-white fw-500"
            onClick={handleResendOtp}
            disabled={loading || countdown > 0}
          >
            {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="px-4 pb-3"
      style={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      <div className=" d-flex flex-column mb-3">
        <Logo />

        <div>
          <button
            onClick={() => setCurrentStep(2)}
            className="btn btn-link text-decoration-none p-0 fw-semibold fs-14 mt-3"
            style={{ color: "#47BBA9" }}
          >
            ← Back
          </button>
        </div>
      </div>
      <div className="mb-4">
        <h4 className="fw-medium font-onest mb-2">Set your password</h4>
        <p className="text-light-black fs-16 mb-4 font-onest">
          Enter your preferred password below
        </p>
      </div>

      <div ref={errorRef}>
        <ErrorDisplay
          errors={
            Array.isArray(error)
              ? error
              : error
                ? [{ message: String(error), field: "general" }]
                : []
          }
        />
      </div>
      <SuccessDisplay message={success} />

      <form className="mb-children mt-3" onSubmit={handleFinalSubmit}>
        <div className="form-group col mb-3">
          <label className="text-uppercase fw-600 fs-12 m-1" htmlFor="password">
            Password
          </label>
          <div className="input-group">
            <span
              className="input-group-text input-group-text-0"
              id="basic-addon1"
            >
              <i className="iconly-Lock icli fs-4"></i>
            </span>
            <input
              type={showPassword ? "text" : "password"}
              className="form-control form-control-0 ps-3 py-3 border-start-0"
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ borderRight: "none" }}
            />
            <span className="input-group-text input-group-text-0">
              {showPassword ? (
                <i
                  className="iconly-Show icli fs-4"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: "pointer", color: "#05569f" }}
                ></i>
              ) : (
                <i
                  className="iconly-Hide icli fs-4"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: "pointer", color: "#05569f" }}
                ></i>
              )}
            </span>
          </div>
          <small
            className={`d-block mt-1 ${passwordIsValid ? "text-muted" : "text-danger"}`}
          >
            {passwordValidationError || PASSWORD_REQUIREMENTS_HINT}
          </small>
        </div>
        <div className="form-group col mb-4">
          <label
            className="text-uppercase fw-600 fs-12 m-1"
            htmlFor="confirmPassword"
          >
            Confirm Password
          </label>
          <div className="input-group">
            <span
              className="input-group-text input-group-text-0"
              id="basic-addon2"
            >
              <i className="iconly-Lock icli fs-4"></i>
            </span>
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="form-control form-control-0 ps-3 py-3 border-start-0"
              id="confirmPassword"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={{ borderRight: "none" }}
            />
            <span className="input-group-text input-group-text-0">
              {showConfirmPassword ? (
                <i
                  className="iconly-Show icli fs-4"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{ cursor: "pointer", color: "#05569f" }}
                ></i>
              ) : (
                <i
                  className="iconly-Hide icli fs-4"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{ cursor: "pointer", color: "#05569f" }}
                ></i>
              )}
            </span>
          </div>
          {confirmPassword && !passwordsMatch ? (
            <small className="text-danger d-block mt-1">
              Passwords do not match
            </small>
          ) : null}
        </div>

        <div className="form-check mt-4 mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="agreeTerms"
            checked={agreeToTerms}
            onChange={(e) => setAgreeToTerms(e.target.checked)}
            required
          />
          <label
            className="form-check-label fs-14"
            htmlFor="agreeTerms"
            style={{ cursor: "pointer" }}
          >
            By signing up you agree to our{" "}
            <span
              onClick={() => {
                onClose();
                navigate("/terms");
              }}
              className="text-primary fw-bold"
              style={{ cursor: "pointer" }}
            >
              Terms of service
            </span>{" "}
            and{" "}
            <span
              onClick={() => {
                onClose();
                navigate("/privacy");
              }}
              className="text-primary fw-bold"
              style={{ cursor: "pointer" }}
            >
              Privacy policy
            </span>
            .
          </label>
        </div>

        <div className="d-grid">
          <PrimaryButton
            type="primary"
            text="Proceed"
            loading={loading}
            disable={!canSubmitPassword}
          />
        </div>
      </form>
    </div>
  );
};
